import React, { memo } from 'react'
import { TableCell, TableRow, Card } from '@mui/material'
import { varAlpha } from 'minimal-shared/utils'

import Text from '../shared/text/Text'
import CustomDataTable from '../shared/customDataTable/CustomDataTable'
import DisplayBadge from './DisplayBadge'
import {
  CURRENCIES,
  membershipLevelLabel
} from '../../util/staking/staking.helpers'
import brandConfig from 'brandConfig'
import { formatCurrencyShort } from '../../util/number.helpers'
import { PERKS_PRICING } from '../../util/staking/perks.helpers'
import { formatTicks } from '../../util/string.helpers'

const MembershipDataTable = ({ tiers, currentTier, t }) => {
  const symbol = CURRENCIES.VIR
  const badges = tiers.map(({ id, name }) => ({
    label: <DisplayBadge key={`lb${id}`} name={name} id={id} size='sm' />,
    align: 'center',
    id
  }))
  const supportFill = ['6th', '5th', '4th', '3rd', '2nd', '1st']
  const data = [
    {
      section: '',
      data: [
        [
          {
            title: t('membership.status-levels'),
            sub: brandConfig.name
          },
          ...tiers.map(({ id }) => membershipLevelLabel({ t, id }))
        ]
      ]
    },
    {
      section: t('membership.requirements'),
      data: [
        [
          {
            title: t('membership.value-of-staked'),
            sub: t('membership.value-of-subtitle')
          },
          ...tiers.map(({ fiatThreshold }) =>
            fiatThreshold === 0
              ? t('membership.free')
              : `$${formatCurrencyShort(fiatThreshold)}`
          )
        ]
      ]
    },
    {
      section: t('membership.exclusive-perks'),
      data: [
        [
          {
            title: t('membership.staking-reward-in', {
              symbol: CURRENCIES.QBX
            }),
            sub: t('membership.staking-reward-in-sub')
          },
          '-',
          '4%',
          '6%',
          '8%',
          '10%',
          '12%'
        ],
        ...PERKS_PRICING.map(({ id, price }) => {
          return [
            {
              title: t(`brand:perk.list.${id}.title`),
              sub: t(`brand:perk.membership-desc`)
            },
            ...price.map((amount) =>
              amount > 0 ? `$${formatTicks(amount)}` : '-'
            )
          ]
        }),
        [
          {
            title: t('membership.web3-events'),
            sub: t('membership.web3-events-sub')
          },
          '-',
          '-',
          '-',
          t('membership.yes'),
          t('membership.yes'),
          t('membership.yes')
        ],
        [
          {
            title: t('membership.excel-exp'),
            sub: t('membership.excel-exp-sub')
          },
          '-',
          '-',
          '-',
          '-',
          t('membership.yes'),
          t('membership.yes')
        ]
      ]
    },
    {
      section: t('membership.earn-points'),
      data: [
        [
          {
            title: t('membership.staking-reward-in', { symbol }),
            sub: t('membership.claimable-daily')
          },
          '-',
          ...Array(5).fill(t('membership.yes'))
        ],
        [
          {
            title: t('membership.quests'),
            sub: t('membership.earn-limit-month')
          },
          ...[0, 100, 1000, 10000, 50000, 100000].map((value) =>
            formatTicks(value)
          )
        ],
        [
          {
            title: t('games.play-games'),
            sub: t('membership.play-games-sub')
          },
          ...columnLabelFill(t)
        ],
        [
          {
            title: t('earn-via.post-to-earn'),
            sub: t('membership.post-to-earn-sub')
          },
          ...[1, 1, 2, 2, 3, 3]
        ],

        [
          {
            title: t('membership.cashback-in'),
            sub: t('membership.bonus-on-top')
          },
          '-',
          '10%',
          '20%',
          '30%',
          '40%',
          '50%'
        ],
        [
          {
            title: t('games.travel-and-earn'),
            sub: t('membership.travel-and-earn-sub')
          },
          ...columnLabelFill(t)
        ],
        [
          {
            title: t('referral.invite-friends'),
            sub: t('membership.invite-friends-sub')
          },
          ...tiers.map(({ metadata }) => metadata?.referralMultiplier || 0)
        ]
      ]
    },
    {
      section: t('membership.spend-points'),
      data: [
        [
          {
            title: t('navigation.shop'),
            sub: t('membership.shop-sub')
          },
          ...columnLabelFill(t)
        ],
        [
          {
            title: t('membership.exchange-to-points', { symbol }),
            sub: t('membership.exchange-to-points-sub')
          },
          '-',
          ...columnLabelFill(t, 5)
        ],
        [
          {
            title: t('navigation.vip-store'),
            sub: t('membership.vip-store-sub')
          },
          '-',
          '-',
          '-',
          ...columnLabelFill(t, 3)
        ],
        [
          {
            title: t('membership.exchange-to-crypto', { symbol }),
            sub: t('membership.exchange-to-crypto-sub')
          },
          '-',
          '-',
          '-',
          ...columnLabelFill(t, 3)
        ]
      ]
    },
    {
      section: t('membership.services'),
      data: [
        [
          {
            title: t('membership.email-support'),
            sub: t('membership.priority-based')
          },
          ...supportFill
        ],
        [
          {
            title: t('membership.discord-ticket-support'),
            sub: t('membership.priority-based')
          },
          ...supportFill
        ]
      ]
    }
  ]
  const columnWidths = [
    '23%',
    '12.83%',
    '12.83%',
    '12.83%',
    '12.83%',
    '12.83%',
    '12.83%'
  ]
  const styles = {
    tableContainer: {
      maxHeight: 680,
      maxWidth: '100%',
      overflow: 'auto'
    },
    headerColumn: {
      position: 'sticky',
      top: 0,
      left: 0,
      zIndex: 3
    },
    headerRow: {
      position: 'sticky',
      top: 0,
      zIndex: 2,
      backgroundColor: 'var(--palette-background-paper)'
    },
    tableCell: {
      position: 'sticky',
      left: 0,
      zIndex: 1,
      backgroundColor: 'var(--palette-background-paper)',
      textAlign: 'left'
    },
    sectionRow: {
      background:
        'linear-gradient(to bottom, var(--palette-background-neutral), var(--palette-background-neutral))'
    },
    sectionCell: {
      position: 'sticky',
      left: 0,
      zIndex: 1,
      backgroundColor: 'var(--palette-background-neutral)'
    }
  }
  return (
    <Card>
      <CustomDataTable
        tableHeaders={[
          {
            id: 0,
            label: '',
            align: 'left'
          },
          ...badges
        ].map((props, index) => ({
          ...props,
          sx: {
            ...(index === 0 ? styles.headerColumn : styles.headerRow),
            whiteSpace: 'nowrap',
            borderBottom:
              '1px dashed var(--palette-TableCell-border) !important'
          }
        }))}
        columnWidths={columnWidths}
        stickyHeader
        sx={{
          ...styles.tableContainer
        }}
      >
        {data.map(({ section, data }, sectionIndex) => (
          <React.Fragment key={sectionIndex}>
            {section && (
              <TableRow sx={styles.sectionRow}>
                <TableCell colSpan={2} sx={styles.sectionCell}>
                  <Text variant='subtitle1' label={section} />
                </TableCell>
                <TableCell colSpan={5}></TableCell>
              </TableRow>
            )}
            {data.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    sx={{
                      bgcolor: (theme) =>
                        currentTier === cellIndex &&
                        varAlpha(theme.palette.primary.mainChannel, 0.08),
                      textAlign: 'center',
                      width: columnWidths[cellIndex],
                      ...(cellIndex === 0 && styles.tableCell)
                    }}
                  >
                    {typeof cell === 'object' ? (
                      cell.img ? (
                        cell.img
                      ) : (
                        <CellHeading {...cell} />
                      )
                    ) : (
                      <Text variant='subtitle2' label={cell} />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </React.Fragment>
        ))}
      </CustomDataTable>
    </Card>
  )
}

export default memo(MembershipDataTable)

const CellHeading = ({ title, sub, sx }) => {
  return (
    <>
      <Text label={title} fontWeight={600} sx={sx} />
      <Text label={sub} variant='caption' sx={sx} />
    </>
  )
}

const columnLabelFill = (t, count = 6) => {
  return Array(count).fill(t('membership.yes'))
}
